// ===============================================================
// File: src/contexts/UtilitiesContext.jsx
// Description: Drive/Folder-first Utilities store (mudoUtilities.json)
// - Uses StorageContext readDomain/writeDomain("utilities_file")
// - No dependency on hasFolder (works for Drive too)
// - Auto-refresh on "mudo:vault-restored" / "mudo:files-restored"
// - Optional localStorage mirror for legacy readers ("mudoUtilities")
// ===============================================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import { useStorage } from "@/contexts/StorageContext";

const LOCAL_MIRROR_KEY = "mudoUtilities"; // legacy compatibility

export const UtilitiesContext = createContext({
  utilities: [],
  setUtilities: () => {},
  addOrUpdateUtility: () => {},
  removeUtility: () => {},
  clearUtilities: () => {},
});

function normalizeUtility(u) {
  const now = Date.now();
  const id = String(u?.id || `${now}_${Math.random().toString(36).slice(2, 8)}`);
  return {
    id,
    name: String(u?.name || "").trim() || "Utility",
    type: String(u?.type || u?.category || "").trim(),
    cost: Number(u?.cost ?? 0) || 0,
    frequency: u?.frequency || "Monthly",
    notes: String(u?.notes || ""),
    createdAt: Number(u?.createdAt) || now,
    updatedAt: now,
  };
}

const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
};

export function UtilitiesProvider({ children }) {
  const storage = useStorage();
  const [utilities, _setUtilities] = useState([]);
  const loadedRef = useRef(false);

  // ---- Load on mount / storage ready (Drive or Folder) ----
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      try {
        // Canonical domain file
        const data = await storage.readDomain?.("utilities_file", []);
        const seed = Array.isArray(data)
          ? data
          : JSON.parse(localStorage.getItem(LOCAL_MIRROR_KEY) || "[]");

        if (alive) {
          _setUtilities((seed || []).map(normalizeUtility));
        }
      } catch (e) {
        console.warn("[Utilities] initial load failed:", e);
        if (alive) _setUtilities([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => {
      alive = false;
    };
  }, [storage]);

  // ---- Persist on change (domain file + optional local mirror) ----
  useEffect(() => {
    if (!loadedRef.current) return;
    (async () => {
      try {
        await storage.writeDomain?.("utilities_file", utilities);
      } catch (e) {
        console.warn("[Utilities] writeDomain failed:", e);
      }
      try {
        localStorage.setItem(LOCAL_MIRROR_KEY, JSON.stringify(utilities));
      } catch {}
      window.dispatchEvent?.(new Event("mudo:utilities-updated"));
    })();
  }, [utilities, storage]);

  // ---- Refresh on Receive events (vault/files) ----
  useEffect(() => {
    const refresh = async () => {
      try {
        const data = await storage.readDomain?.("utilities_file", []);
        _setUtilities(Array.isArray(data) ? data.map(normalizeUtility) : []);
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      _setUtilities([]);
    };
    
    window.addEventListener("mudo:vault-restored", refresh);
    window.addEventListener("mudo:files-restored", refresh);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", refresh);
      window.removeEventListener("mudo:files-restored", refresh);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [storage]);

  // ---- API ----
  const setUtilities = useCallback((nextArray) => {
    if (!Array.isArray(nextArray)) return;
    _setUtilities(nextArray.map(normalizeUtility));
  }, []);

  const addOrUpdateUtility = useCallback((u) => {
    if (!u) return;
    const incoming = normalizeUtility(u);
    _setUtilities((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((x) => String(x.id) === String(incoming.id));
      if (idx >= 0) {
        const merged = { ...list[idx], ...incoming, id: list[idx].id, updatedAt: Date.now() };
        if (shallowEqual(list[idx], merged)) return prev;
        const next = [...list];
        next[idx] = merged;
        return next;
      }
      return [incoming, ...list];
    });
  }, []);

  const removeUtility = useCallback((id) => {
    _setUtilities((prev) => (Array.isArray(prev) ? prev.filter((x) => String(x.id) !== String(id)) : prev));
  }, []);

  const clearUtilities = useCallback(() => {
    _setUtilities([]);
    (async () => {
      try {
        await storage.writeDomain?.("utilities_file", []);
      } catch {}
      try {
        localStorage.setItem(LOCAL_MIRROR_KEY, "[]");
      } catch {}
    })();
  }, [storage]);

  const value = useMemo(
    () => ({
      utilities,
      setUtilities,
      addOrUpdateUtility,
      removeUtility,
      clearUtilities,
    }),
    [utilities, setUtilities, addOrUpdateUtility, removeUtility, clearUtilities]
  );

  return <UtilitiesContext.Provider value={value}>{children}</UtilitiesContext.Provider>;
}

export function useUtilities() {
  return useContext(UtilitiesContext);
}
